import React, { forwardRef } from 'react';
import { KeyRound, Package, Rocket } from 'lucide-react';
import './OpenClawHowItWorks.css';

/**
 * OpenClawHowItWorks — §3.
 *
 * Forwarded ref so the parent's "See how it works" secondary CTA can
 * smooth-scroll to this section's root.
 *
 * Three vertical step bands, each with a number badge, title, body, and
 * a tiny "code hint" chip (optional) for concreteness without overloading.
 */
const STEPS = [
  {
    number: '01',
    icon: <KeyRound size={20} />,
    title: 'Generate a CellCog API key',
    body: 'One key authenticates every OpenClaw agent that delegates to CellCog. Create it once, reuse across agents, environments, and deployments.',
    hint: 'CellCog profile → API Keys',
  },
  {
    number: '02',
    icon: <Package size={20} />,
    title: 'Ask your agent to install CellCog — two paths',
    body: 'ClawHub skill install is the lightweight option: the skill bundle teaches your agent every CellCog capability. OpenClaw plugin install gives you skills plus routing and setup bundled — richer integration when you want CellCog as a first-class part of your environment.',
    hint: 'openclaw skills install @cellcog/cellcog · OR · openclaw plugins install clawhub:@cellcog/openclaw-plugin',
  },
  {
    number: '03',
    icon: <Rocket size={20} />,
    title: 'Delegate from your agent',
    body: 'Your OpenClaw agent calls CellCog through the Python SDK. Two delivery modes — wait-for-completion for quick tasks, daemon-based notify-on-completion for long-running work. Automatic interim progress updates every 4 minutes so nothing feels stuck.',
    hint: 'client.create_chat(prompt=…, notify_session_key=…)',
  },
];

const OpenClawHowItWorks = forwardRef((props, ref) => {
  return (
    <section ref={ref} className="openclaw-how" id="openclaw-how-it-works">
      <div className="section">
        <div className="section-eyebrow">How it works</div>
        <h2 className="section-title display-font">
          Three steps. Then your agents can do anything CellCog can.
        </h2>
        <p className="section-subtitle">
          No framework migration, no per-capability plumbing, no streaming
          handshake to implement. You add an API key. The rest happens.
        </p>

        <div className="openclaw-how__steps">
          {STEPS.map((s) => (
            <div key={s.number} className="openclaw-how__step">
              <div className="openclaw-how__step-head">
                <div className="openclaw-how__step-badge">
                  <span className="openclaw-how__step-num">{s.number}</span>
                  <span className="openclaw-how__step-icon">{s.icon}</span>
                </div>
                <h3 className="openclaw-how__step-title">{s.title}</h3>
              </div>
              <p className="openclaw-how__step-body">{s.body}</p>
              {s.hint && (
                <div className="openclaw-how__step-hint">
                  <code>{s.hint}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

OpenClawHowItWorks.displayName = 'OpenClawHowItWorks';

export default OpenClawHowItWorks;
